import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { Select, Spin } from 'antd';

import { useGetCountriesBySearchQuery } from 'lib.store/src/main/countries/countries.api';
import { INIT_CURRENT_PAGE } from 'lib.shared/src/constants';

type SelectOption = { label: string; value: string };

interface SuggestSelectCountriesProps {
  value: {
    id: number;
    slug: string;
  } | null;
  onChange?: (
    value: {
      id: number;
      slug: string;
    } | null,
  ) => void;
  placeholder: string;
  disabled?: boolean;
  'data-test-id'?: string;
}

const SuggestSelectCountries: React.FC<SuggestSelectCountriesProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  'data-test-id': dataTestId,
}) => {
  const [countrySearchValue, setCountrySearchValue] = useState<string>('');
  const [page, setPage] = useState(INIT_CURRENT_PAGE);
  const [countriesOptions, setCountriesOptions] = useState<SelectOption[]>([]);

  const {
    data: countries,
    isLoading: countriesLoading,
    isFetching: countriesFetching,
  } = useGetCountriesBySearchQuery({ search: countrySearchValue || '', page });

  useEffect(() => {
    if (!countries?.data) {
      if (value && value.slug) {
        setCountriesOptions([{ value: value.id.toString(), label: value.slug }]);
      } else {
        setCountriesOptions([]);
      }
      return;
    }
    const mapped = countries.data.map(({ slug, id }) => ({
      value: id.toString(),
      label: slug,
    }));

    setCountriesOptions((prev) => {
      let options: SelectOption[];
      if (page === INIT_CURRENT_PAGE) {
        options = mapped;
      } else {
        const byValue = new Map(prev.map((o) => [o.value, o]));
        mapped.forEach((o) => byValue.set(o.value, o));
        options = [...byValue.values()];
      }
      if (value && value.slug) {
        const valueStr = value.id.toString();
        const existsInOptions = options.some((o) => o.value === valueStr);
        if (!existsInOptions) {
          options = [{ value: valueStr, label: value.slug }, ...options];
        }
      }
      return options;
    });
  }, [countries, page, value]);

  const onPopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 2;
      const lastPage = countries?.meta?.last_page ?? 0;
      if (nearBottom && page < lastPage && !countriesLoading && !countriesFetching) {
        setPage((p) => p + 1);
      }
    },
    [page, countries?.meta?.last_page, countriesLoading, countriesFetching],
  );

  const handleChange = useCallback(
    (selectedValue: string | null, option: { value: string; label: string } | undefined) => {
      if (!selectedValue || !option) {
        onChange?.(null);
        return;
      }
      onChange?.({
        id: Number(selectedValue),
        slug: option.label,
      });
      setCountrySearchValue('');
    },
    [onChange],
  );

  const selectValue = useMemo(() => (value ? value.id.toString() : undefined), [value]);

  const isLoading = countriesLoading || countriesFetching;

  return (
    <Select
      id="suggest-select-countries"
      placeholder={placeholder}
      style={{ width: '100%' }}
      filterOption={false}
      notFoundContent={isLoading ? <Spin size="small" /> : <div>There is no countries</div>}
      loading={isLoading}
      dropdownStyle={{ maxHeight: 190 }}
      options={countriesOptions}
      value={selectValue}
      onChange={(val) => {
        const option = countriesOptions.find((o) => o.value === val);
        handleChange(val ?? null, option);
      }}
      onSearch={(val) => {
        setCountrySearchValue(val || '');
        setPage(INIT_CURRENT_PAGE);
      }}
      onOpenChange={(open) => {
        if (!open) {
          setPage(INIT_CURRENT_PAGE);
        }
      }}
      searchValue={countrySearchValue}
      allowClear
      showSearch
      disabled={disabled}
      data-test-id={dataTestId}
      onPopupScroll={onPopupScroll}
    />
  );
};

export default SuggestSelectCountries;
